import { createContext, useContext, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router';
import { roleCan, supportSessionCan, type SupportScope, type TenantPermission, type TenantRole } from '@zerogap/shared';
import { ApiError, get, setCsrf } from './api';

export interface Me {
  user: { id: string; email: string; name: string; emailVerified: boolean; mfaEnabled: boolean; hasPassword: boolean; googleLinked: boolean };
  mfaPending: boolean;
  isSuperAdmin: boolean;
  csrfToken: string;
  stepUpValidUntil: string | null;
  memberships: { tenantId: string; role: TenantRole; tenantName: string; tenantStatus: string; mfaRequired: boolean }[];
  supportSessions: { id: string; tenantId: string; tenantName: string; scope: SupportScope; reason: string; expiresAt: string }[];
}

const Ctx = createContext<{ me: Me | null; loading: boolean; refresh: () => Promise<void> }>({ me: null, loading: true, refresh: async () => {} });

export function SessionProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['me'],
    queryFn: async () => {
      try {
        const me = await get<Me>('/api/me');
        setCsrf(me.csrfToken);
        return me;
      } catch (e) {
        if ((e as ApiError).status === 401) return null;
        throw e;
      }
    },
    staleTime: 60_000,
    retry: false,
  });
  return (
    <Ctx.Provider value={{ me: q.data ?? null, loading: q.isLoading, refresh: async () => void (await qc.invalidateQueries({ queryKey: ['me'] })) }}>
      {children}
    </Ctx.Provider>
  );
}

export const useSession = () => useContext(Ctx);

/** Current tenant access from the URL: membership role, or an operator support session. */
export function useTenant() {
  const { tenantId } = useParams();
  const { me } = useSession();
  const m = me?.memberships.find((x) => x.tenantId === tenantId);
  const s = me?.supportSessions.find((x) => x.tenantId === tenantId);
  const can = (p: TenantPermission) => (m ? roleCan(m.role, p) : s ? supportSessionCan(s.scope, p) : false);
  return {
    tenantId: tenantId!,
    name: m?.tenantName ?? s?.tenantName ?? '',
    role: m?.role ?? null,
    support: s ?? null,
    status: m?.tenantStatus ?? 'active',
    can,
    base: `/t/${tenantId}`,
  };
}
